<template>
  <div class="rsm-post" ref="postEl" @touchstart="handleDoubleTap" @dblclick="handleDoubleLike">
    <div class="rsm-post__mute">
      <!-- Image media -->
      <img
        v-if="post.mediaType !== 'video'"
        class="rsm-post__image"
        :src="post.mediaUrl"
        :alt="post.caption"
        loading="lazy"
      />

      <!-- Video media (Vimeo) — lazy: iframe only mounts when post is active -->
      <template v-else>
        <!-- Transparent touch blocker: prevents iframe consuming swipe events on mobile.
             touch-action: pan-y tells the browser to pass vertical swipes to the scroll container. -->
        <!-- touchstart.stop prevents bubbling to rsm-post; click.stop handles desktop.
             onVideoTap guards against mobile double-firing (touchstart + synthetic click). -->
        <div class="rsm-post__touch-blocker" @touchstart.stop="onVideoTap" @touchend.stop="onVideoTap" @click.stop="onVideoTap"></div>

        <!-- Pause indicator: shown while video is paused by user -->
        <div v-if="videoPaused" class="rsm-post__pause-icon">
          <svg viewBox="0 0 24 24" fill="white" width="60" height="60"><path d="M8 5v14l11-7z"/></svg>
        </div>

        <!-- Mute/unmute (TikTok pattern: start muted, tap to unmute) -->

        <button class="rsm-post__mute-btn" @touchstart.stop @click.stop="toggleMute" :aria-label="store.isMuted ? 'Activer le son' : 'Couper le son'">
          <svg v-if="store.isMuted" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" width="20" height="20"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
          <svg v-else viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg" width="20" height="20"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
        </button>
      </template>
    </div>

    <div class="rsm-post__info">
    <!-- Debug: harmful post indicator -->
      <div v-if="isDebug && post.harmful" class="rsm-debug-harmful">HARMFUL</div>

      <!-- Bottom text overlay -->
      <div class="rsm-post__overlay">
        <div class="rsm-post__username">@{{ post.profileName }}</div>
        <div class="rsm-post__caption" :class="{ 'rsm-post__caption--expanded': captionExpanded }">{{ post.caption }}</div>
        <button v-if="!captionExpanded && post.caption && post.caption.length > 80" class="rsm-post__more" @click.stop="captionExpanded = true">...voir plus</button>
        <button v-if="captionExpanded" class="rsm-post__more" @click.stop="captionExpanded = false">voir moins</button>
      </div>

      <!-- Right-side action buttons -->
      <div class="rsm-post__actions" @dblclick.stop>
        <button class="rsm-action" @click="handleLike" :aria-label="liked ? 'Ne plus aimer' : 'Aimer'">
          <span class="rsm-action__icon">
            <svg v-if="liked" viewBox="0 0 24 24" fill="#ff2d55" xmlns="http://www.w3.org/2000/svg"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" xmlns="http://www.w3.org/2000/svg"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          </span>
          <span class="rsm-action__count">{{ formattedLikes }}</span>
        </button>

        <button class="rsm-action" @click="handleInteraction('comment')" aria-label="Commenter">
          <span class="rsm-action__icon">
            <svg v-if="commented" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" xmlns="http://www.w3.org/2000/svg"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          </span>
          <span class="rsm-action__count">{{ post.comments }}</span>
        </button>

        <button class="rsm-action" @click="handleInteraction('share')" aria-label="Partager">
          <span class="rsm-action__icon">
            <svg v-if="shared" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2" xmlns="http://www.w3.org/2000/svg"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" xmlns="http://www.w3.org/2000/svg"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </span>
          <span class="rsm-action__count">Partager</span>
        </button>

        <button class="rsm-action" @click.stop="showReport = true" aria-label="Signaler">
          <span class="rsm-action__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.8" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/></svg>
          </span>
          <span class="rsm-action__count">Signaler</span>
        </button>
      </div>

      <!-- Report reason sheet -->
      <div v-if="showReport" class="rsm-report-overlay" @click.self="showReport = false">
        <div class="rsm-report-sheet">
          <template v-if="reportConfirmed">
            <div class="rsm-report-title">Merci pour le signalement ✅</div>
          </template>
          <template v-else>
            <div class="rsm-report-title">Signaler</div>
            <button class="rsm-report-option" @click="handleReport('Je ne l\'apprécie pas')">Je ne l'apprécie pas</button>
            <button class="rsm-report-option" @click="handleReport('Contenu de marque non divulgué')">Contenu de marque non divulgué</button>
            <button class="rsm-report-option" @click="handleReport('Promotion de pratiques ou produits potentiellement dangereux')">Promotion de pratiques ou produits potentiellement dangereux</button>
            <button class="rsm-report-cancel" @click="showReport = false">Retour</button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import store from '../../store.js';

export default {
  props: {
    post:    { type: Object, required: true },
    isDebug: { type: Boolean, default: false },
    paused:  { type: Boolean, default: false },
  },
  data() {
    return {
      store,
      liked: false,
      commented: false,
      shared: false,
      captionExpanded: false,
      showReport: false,
      reportConfirmed: false,
      lastTap: 0,
      videoPaused: false,
      viewStartTime: null,
      viewTimer: null,
      observer: null,
      _touchStartX: 0,
      _touchStartY: 0,
    };
  },
  computed: {
    formattedLikes() {
      const n = this.post.likes || 0;
      return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n);
    },
  },
  methods: {
    handleDoubleTap() {
      const now = Date.now();
      clearTimeout(this._tapTimer);
      this._tapTimer = null;
      if (now - this.lastTap < 300) {
        if (!this.liked) this.handleLike();
      }
      this.lastTap = now;
    },
    handleDoubleLike() {
      if (!this.liked) this.handleLike();
    },
    handleLike() {
      this.liked = !this.liked;
      this.$emit('interaction', { type: 'like', postId: this.post.id, value: this.liked });
    },
    handleInteraction(type) {
      if (type === 'comment') this.commented = !this.commented;
      if (type === 'share') this.shared = !this.shared;
      this.$emit('interaction', { type, postId: this.post.id });
    },
    handleReport(reason) {
      this.$emit('interaction', { type: 'report', postId: this.post.id, value: reason });
      this.reportConfirmed = true;
      setTimeout(() => {
        this.showReport = false;
        this.reportConfirmed = false;
      }, 1500);
    },
    _sendMuteCmd() {
      this.$emit('toggle-mute');
    },
    onVideoTap(e) {
      // On mobile, touchstart fires then a synthetic click follows within ~500ms.
      // Ignore the synthetic click when a touchstart was already handled recently.
      // It also fires for swipe events so check the position of start/end and if it's changed,
      // assume swipe and ignore it
      if (e.type === 'touchstart') {
        this._lastTouchStart = Date.now();
        const t = e.touches[0];
        this._touchStartX = t.clientX;
        this._touchStartY = t.clientY;
        return;
      } else if (e.type === 'click' && Date.now() - (this._lastTouchStart || 0) < 500) {
        return;
      }

      if (e.type === 'touchend') {
        const t = e.changedTouches[0];
        const dx = Math.abs(t.clientX - this._touchStartX);
        const dy = Math.abs(t.clientY - this._touchStartY);
        if (dx > 10 || dy > 10) return;
      }

      const now = Date.now();
      if (now - this.lastTap < 300) {
        clearTimeout(this._tapTimer);
        this._tapTimer = null;
        if (!this.liked) this.handleLike();
      } else {
        clearTimeout(this._tapTimer);
        this._tapTimer = setTimeout(() => {
          this.togglePlayPause();
          this._tapTimer = null;
        }, 300);
      }
      this.lastTap = now;
    },
    togglePlayPause() {
      this.videoPaused = !this.videoPaused;
      this.$emit('toggle-pause', this.videoPaused);
      // Exposure to harmful content shouldn't count while the participant has paused the video —
      // otherwise a non-complier could pause on the first video and let the threshold tick by unexposed.
      if (this.videoPaused) {
        this._stopHarmfulTracking();
      } else {
        this._startHarmfulTracking();
      }
    },
    _startHarmfulTracking() {
      if (!this.post.harmful || this._harmfulInterval) return;
      this._harmfulLastEmit = Date.now();
      this._harmfulInterval = setInterval(() => {
        const now = Date.now();
        // Don't count time while the tab is hidden: the participant isn't watching, and
        // wall-clock deltas would otherwise let harmful time exceed total feed time.
        if (document.hidden) {
          this._harmfulLastEmit = now;
          return;
        }
        this.$emit('harmful-time-update', now - this._harmfulLastEmit);
        this._harmfulLastEmit = now;
      }, 500);
    },
    _stopHarmfulTracking() {
      if (!this._harmfulInterval) return;
      clearInterval(this._harmfulInterval);
      this._harmfulInterval = null;
      this.$emit('harmful-time-update', Date.now() - this._harmfulLastEmit);
      this._harmfulLastEmit = null;
    },
    _applyMuteState() {
      this._sendMuteCmd();
    },
    toggleMute() {
      clearTimeout(this._tapTimer);
      this._tapTimer = null;
      this.store.isMuted = !this.store.isMuted;
      this._sendMuteCmd();
    },
  },
  watch: {
    // When an overlay (chatbot / inoculation) opens, the feed is paused: stop counting
    // harmful exposure so it can't accrue while total feed time is frozen. Resume when it closes.
    paused(isPaused) {
      if (isPaused) this._stopHarmfulTracking();
      else if (this._isIntersecting && !this.videoPaused) this._startHarmfulTracking();
    },
  },
  mounted() {
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this._isIntersecting = entry.isIntersecting;
        if (entry.isIntersecting) {
          if (this.post.mediaType === 'video') {
            this.videoPaused = false;
            this._isReplay = (this._playCount || 0) > 0;
            this._playCount = (this._playCount || 0) + 1;
            this._videoActiveStart = Date.now();
            this.$emit('video-activated', this.post);
          }
          if (!this.viewStartTime) {
            this.viewStartTime = Date.now();
            this.viewTimer = setTimeout(() => {
              this.$emit('post-seen', this.post);
            }, 3000);
          }
          if (!this.paused) this._startHarmfulTracking();
        } else {
          if (this.post.mediaType === 'video' && this._videoActiveStart) {
            const duration = Date.now() - this._videoActiveStart;
            this.$emit('video-event', {
              postId: this.post.id,
              videoId: this.post.youtubeId || this.post.id,
              timeStart: this._videoActiveStart,
              timeEnd: Date.now(),
              duration,
              completed: duration >= 15000,
              replayed: this._isReplay || false,
            });
            this._videoActiveStart = null;
          }
          this._stopHarmfulTracking();
          clearTimeout(this.viewTimer);
          this.viewStartTime = null;
        }
      },
      { threshold: 0.7 }
    );
    this.observer.observe(this.$refs.postEl);
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.viewTimer);
    clearTimeout(this._tapTimer);
    clearInterval(this._harmfulInterval);
    this._videoActiveStart = null;
  }
};
</script>
